import React from 'react';
import { Formik, useFormikContext } from 'formik';
import { noop, snakeCase } from 'lodash';
import * as Yup from 'yup';

import CardDialog from '~/components/CardDialog';
import ColoredDot, { CategoriesColors } from '~/components/core/Atomic/ColoredDot';
import MenuItem from '~/components/core/Atomic/MenuItem';
import DialogFooterActions from '~/components/core/DialogFooterActions';
import type { CategoryModel } from '~/components/SystemConfiguration/NotificationsConfiguration/NotificationsCategories';
import TextFieldFormik, { useSetDefaultFieldsOnChange } from '~/components/TextFieldFormik';

interface EditCategoryDialogProps {
  category?: CategoryModel;
  onCancel: () => void;
  onSubmit: (category: Partial<CategoryModel>) => Promise<void>;
  existingKeys: string[];
  showDescription?: boolean;
}

const AddEditCategoryDialog: React.FC<EditCategoryDialogProps> = ({
  category,
  onCancel = noop,
  onSubmit,
  existingKeys,
  showDescription = false,
}) => {
  const isEdit = !!category;

  const validationSchema = Yup.object().shape({
    color: Yup.string().required('Required'),
    key: Yup.string()
      .required('Required')
      .matches(/^[a-z0-9_]*$/, 'Key must be in snake_case format (lowercase letters, numbers, and underscores only)')
      .test(
        'no-consecutive-underscores',
        'Key cannot contain consecutive underscores',
        (value) => !value || !value.includes('__')
      )
      .test('unique-ids', 'Keys must be unique', (value) => (!value || isEdit ? true : !existingKeys.includes(value))),

    label: Yup.string().required('Required'),
  });

  const initialValues: Partial<CategoryModel> = {
    id: category?.id,
    color: category?.color ?? 'teal-600',
    key: category?.key ?? '',
    label: category?.label ?? '',
  };
  if (showDescription) {
    initialValues.description = category?.description ?? '';
  }

  return (
    <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={onSubmit}>
      <AddEditCategoryInner isEdit={isEdit} onCancel={onCancel} showDescription={showDescription} />
    </Formik>
  );
};

interface AddEditCategoryInnerProps {
  isEdit: boolean;
  onCancel: () => void;
  showDescription?: boolean;
}

const AddEditCategoryInner: React.FC<AddEditCategoryInnerProps> = ({ isEdit, onCancel, showDescription = false }) => {
  const { isSubmitting, handleSubmit, values } = useFormikContext<Partial<CategoryModel>>();

  useSetDefaultFieldsOnChange(values.label, { key: snakeCase(values.label) }, noop, isEdit);

  return (
    <CardDialog
      title={`${isEdit ? 'Edit' : 'Add'} Category`}
      isDialog
      maxWidth="sm"
      fullWidth
      onClose={onCancel}
      preventClose={isSubmitting}
      footerActions={
        <DialogFooterActions
          primaryLabel="Save"
          onClickPrimary={handleSubmit}
          onClickSecondary={onCancel}
          disabled={isSubmitting}
        />
      }
    >
      <div className="grid grid-cols-[40px_1fr_1fr] gap-8">
        <TextFieldFormik id="color" label="Color" select disabled={isSubmitting}>
          {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
          {/*@ts-ignore*/}
          {Object.keys(CategoriesColors).map((color: keyof typeof CategoriesColors) => (
            <MenuItem key={color} value={color}>
              <ColoredDot color={color} />
            </MenuItem>
          ))}
        </TextFieldFormik>
        <TextFieldFormik id="label" label="Label" disabled={isSubmitting} />
        <TextFieldFormik id="key" label="Key" disabled={isSubmitting || isEdit} />
        {showDescription && (
          <TextFieldFormik
            id="description"
            label="Description"
            disabled={isSubmitting}
            multiline
            rows={3}
            className="col-span-full"
          />
        )}
      </div>
    </CardDialog>
  );
};

export default AddEditCategoryDialog;
